import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Briefcase, ListTodo, Users2, ClipboardCheck, ArrowUpRight, Activity, MessageCircle, CheckCircle2, UserPlus, RefreshCw, Megaphone, X, Trash2, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-zinc-100 text-zinc-800 border-zinc-300",
  contacted: "bg-blue-50 text-blue-800 border-blue-200",
  qualified: "bg-amber-50 text-amber-800 border-amber-200",
  proposal: "bg-violet-50 text-violet-800 border-violet-200",
  won: "bg-green-50 text-green-800 border-green-200",
  lost: "bg-red-50 text-red-800 border-red-200",
};

const TASK_STATUS_PILL = {
  todo: "bg-amber-50 text-amber-800 border-amber-300",
  in_progress: "bg-blue-50 text-blue-800 border-blue-300",
  done: "bg-green-50 text-green-800 border-green-300",
};

const ACTIVITY_ICON = {
  lead_created: UserPlus,
  task_done: CheckCircle2,
  checkin: ClipboardCheck,
  whatsapp: MessageCircle,
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [now, setNow] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [annText, setAnnText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Critical first: stats + announcements; activity loaded after to keep first paint fast
      const [s, an] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/announcements/active"),
      ]);
      setStats(s.data);
      setAnnouncements(an.data);
      setNow(new Date());
      setRefreshing(false);
      // Activity loads after so first paint isn't blocked
      api.get("/dashboard/activity").then((r) => setActivity(r.data)).catch(() => {});
    } catch {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    // Light clock tick using ref-only update (no heavy re-render)
    const c = setInterval(() => setNow(new Date()), 30000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [load]);

  if (!stats) return <div className="p-10 label-uppercase">Loading dashboard…</div>;

  const isAdmin = user?.role === "admin";

  const postAnnouncement = async () => {
    if (!annText.trim()) return;
    setPosting(true);
    try {
      await api.post("/announcements", { message: annText });
      toast.success("Announcement broadcast to all employees");
      setAnnText("");
      setShowAnnounce(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setPosting(false); }
  };

  const removeAnnouncement = async (id) => {
    await api.delete(`/announcements/${id}`);
    toast.success("Announcement removed");
    load();
  };

  const metrics = [
    isAdmin && { label: "Employees", value: stats.total_users, icon: Users2, to: "/users" },
    { label: "Total Leads", value: stats.total_leads, icon: Briefcase, to: "/leads" },
    { label: "Open Tasks", value: stats.open_tasks, icon: ListTodo, to: "/tasks" },
    { label: "Present Today", value: stats.present_today, icon: ClipboardCheck, to: "/attendance" },
  ].filter(Boolean);

  const maxLpd = Math.max(...stats.leads_per_day.map((x) => x.count), 1);

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      {/* Announcement banner */}
      {announcements.length > 0 && (
        <div className="border-b border-amber-300 bg-amber-50" data-testid="announcement-banner">
          {announcements.map((a) => (
            <div key={a.id} className="page-px py-3 flex items-start gap-3">
              <span className="bft-blink shrink-0 mt-0.5">
                <Megaphone size={16} className="text-amber-700" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-0.5">
                  Announcement · {a.created_by_name} · {new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="text-sm text-zinc-900">{a.message}</div>
              </div>
              {isAdmin && (
                <button
                  onClick={() => removeAnnouncement(a.id)}
                  data-testid={`delete-announcement-${a.id}`}
                  className="text-amber-700 hover:text-red-700 shrink-0"
                  title="Remove announcement"
                ><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Admin announcement composer */}
      {showAnnounce && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-zinc-300 w-full max-w-lg" data-testid="announcement-form">
            <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Megaphone size={16} className="text-amber-600" />
                <h3 className="font-medium">Broadcast Announcement</h3>
              </div>
              <button onClick={() => setShowAnnounce(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-zinc-600">
                This message will appear on every employee's dashboard until end-of-day, and trigger a notification.
              </p>
              <textarea
                value={annText}
                onChange={(e) => setAnnText(e.target.value)}
                data-testid="announcement-input"
                placeholder="e.g., Office closes early today at 4pm. Submit GSTR-3B drafts before lunch."
                rows={4}
                maxLength={500}
                autoFocus
                className="w-full border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1"
              />
              <div className="text-[10px] text-zinc-400 uppercase tracking-wider text-right">{annText.length}/500</div>
            </div>
            <div className="border-t border-zinc-200 px-6 py-3 flex justify-end gap-2">
              <button onClick={() => setShowAnnounce(false)} className="px-4 py-2 text-xs uppercase tracking-wider border border-zinc-300 hover:bg-zinc-50">Cancel</button>
              <button
                onClick={postAnnouncement}
                disabled={posting || !annText.trim()}
                data-testid="announcement-submit"
                className="px-4 py-2 text-xs uppercase tracking-wider bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send size={11} /> {posting ? "Sending…" : "Broadcast"}
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="border-b border-zinc-200 page-px py-6 sm:py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-uppercase mb-2">Overview</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            {isAdmin ? "Admin Console" : `Hello, ${user.name.split(" ")[0]}`}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {now.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setShowAnnounce(true)}
              data-testid="open-announcement-button"
              className="flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs uppercase tracking-wider hover:bg-amber-100"
            >
              <Megaphone size={13} /> Announce
            </button>
          )}
          <div className="text-right hidden sm:block">
            <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Live · 60s refresh</div>
          </div>
          <button onClick={load} data-testid="refresh-button" className="border border-zinc-300 p-2 hover:bg-zinc-50">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <section className={`grid grid-cols-2 lg:grid-cols-${metrics.length} border-b border-zinc-200`}>
        {metrics.map((m, i) => {
          const Icon = m.icon;
          return (
            <Link
              to={m.to}
              key={m.label}
              className={`p-8 group hover:bg-zinc-50 transition-colors ${i > 0 ? "border-l border-zinc-200" : ""}`}
              data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="label-uppercase">{m.label}</span>
                <Icon size={14} className="text-zinc-400 group-hover:text-[#002FA7] transition-colors" strokeWidth={1.5} />
              </div>
              <div className="text-4xl font-bold tracking-tighter font-mono group-hover:text-[#002FA7] transition-colors">{m.value}</div>
              <div className="label-uppercase mt-2 text-zinc-400 group-hover:text-[#002FA7] transition-colors">View →</div>
            </Link>
          );
        })}
      </section>

      <section className="grid lg:grid-cols-12 border-b border-zinc-200">
        <div className="lg:col-span-7 lg:border-r border-zinc-200 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-medium tracking-tight">Lead Pipeline</h2>
            <Link to="/leads" className="text-xs uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="space-y-2 mb-8">
            {stats.pipeline.map((p) => {
              const max = Math.max(...stats.pipeline.map((x) => x.count), 1);
              const pct = (p.count / max) * 100;
              return (
                <div key={p.status} className="flex items-center gap-4">
                  <div className="w-24 label-uppercase">{p.status}</div>
                  <div className="flex-1 bg-zinc-100 h-6 relative">
                    <div className="h-full bg-ikb" style={{ width: `${pct}%`, transition: "width 400ms ease" }} />
                  </div>
                  <div className="w-10 text-right font-mono text-sm">{p.count}</div>
                </div>
              );
            })}
          </div>

          <div className="label-uppercase mb-3">Leads · Last 7 Days</div>
          <div className="flex items-end gap-2 h-32 border-b border-zinc-200 pb-1">
            {stats.leads_per_day.map((d) => {
              const h = (d.count / maxLpd) * 100;
              const dt = new Date(d.date);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5 group">
                  <div className="text-[10px] font-mono text-zinc-500 group-hover:text-zinc-900">{d.count}</div>
                  <div className="w-full bg-ikb" style={{ height: `${h}%`, minHeight: d.count > 0 ? "2px" : "0", transition: "height 400ms ease" }} />
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">{dt.toLocaleDateString("en", { weekday: "short" })}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 p-6 sm:p-8" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-medium tracking-tight flex items-center gap-2">
              <Activity size={16} className="text-[#002FA7]" /> Recent Activity
            </h2>
          </div>
          <div className="border-t border-zinc-200">
            {activity.length === 0 && (
              <div className="py-8 text-sm text-zinc-500">No recent activity yet.</div>
            )}
            {activity.map((a, idx) => {
              const Icon = ACTIVITY_ICON[a.type] || Activity;
              return (
                <div key={idx} className="flex items-start gap-3 py-2.5 border-b border-zinc-200">
                  <div className="w-7 h-7 bg-zinc-100 flex items-center justify-center mt-0.5">
                    <Icon size={12} className="text-zinc-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.title}</div>
                    <div className="text-xs text-zinc-500 truncate">{a.subtitle}</div>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider whitespace-nowrap">
                    {timeAgo(a.ts, now)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-2">
        <div className="p-6 sm:p-8 lg:border-r border-b lg:border-b-0 border-zinc-200">
          <h2 className="text-lg font-medium tracking-tight mb-4">Recent Leads</h2>
          <div className="border-t border-zinc-200">
            {stats.recent_leads.length === 0 && <div className="py-8 text-sm text-zinc-500">No leads yet.</div>}
            {stats.recent_leads.map((l) => (
              <Link to={`/leads/${l.id}`} key={l.id} className="flex items-center justify-between py-3 border-b border-zinc-200 hover:bg-zinc-50 -mx-2 px-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{l.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{l.company || l.phone}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${STATUS_COLORS[l.status] || STATUS_COLORS.new}`}>
                  {l.status}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <h2 className="text-lg font-medium tracking-tight mb-4">Recent Tasks</h2>
          <div className="border border-zinc-200 overflow-x-auto">
            <div className="min-w-[480px]">
            <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
              <div className="col-span-7">Title</div>
              <div className="col-span-2">Priority</div>
              <div className="col-span-3">Status</div>
            </div>
            {stats.recent_tasks.length === 0 && <div className="px-4 py-8 text-sm text-zinc-500">No tasks yet.</div>}
            {stats.recent_tasks.map((t) => (
              <Link to={`/tasks/${t.id}`} key={t.id} className="grid grid-cols-12 border-b border-zinc-200 px-4 py-3 text-sm hover:bg-zinc-50">
                <div className="col-span-7 truncate">{t.title}</div>
                <div className="col-span-2 capitalize">{t.priority === "low" ? "normal" : t.priority}</div>
                <div className="col-span-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${TASK_STATUS_PILL[t.status] || ""}`}>
                    {t.status.replace("_"," ")}
                  </span>
                </div>
              </Link>
            ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function timeAgo(ts, now) {
  const d = new Date(ts);
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
